import { Component, type ErrorInfo, type ReactElement, type ReactNode } from 'react';
import { Button } from './primitives';

/**
 * The error boundary every Vello web app renders at its root (W8-DRIVER-SHIFT-TRUTH, D442; finding X-08 — "no app has an
 * ErrorBoundary or crash capture"). A render error used to unmount the whole console: a blank page in front of an
 * operator, a receptionist or a merchant. The boundary catches it, records a bounded, redacted report (an error message may
 * quote user input: e-mail addresses and long digit runs are masked) and shows {@link CrashFallback} — one primary "Try
 * again" that remounts the app, and a reload. {@link installWebCrashCapture} records what no boundary sees (uncaught errors
 * and unhandled rejections).
 *
 * The sink is the browser console until a server-side crash collector is chosen (an owner decision; `onCrash` is the
 * seam). Self-contained: this package does not depend on the mobile shell, so the public widget's bundle stays small.
 * The copy follows the page language (`<html lang>`), with English as the neutral fallback.
 */

export interface WebCrashReport {
  readonly kind: 'render' | 'global' | 'unhandled-rejection';
  readonly app: string;
  readonly message: string;
  readonly at: string;
}

const MAX_MESSAGE = 300;

/** Mask what an error message may quote from a person: e-mail addresses and runs of 8+ digits. */
export function redactWebCrashText(text: string): string {
  return text
    .replace(/[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}/g, '<email>')
    .replace(/\+?\d[\d\s().-]{6,}\d/g, (run) => (run.replace(/\D/g, '').length >= 8 ? '<digits>' : run));
}

export function describeWebCrash(error: unknown, kind: WebCrashReport['kind'], app: string): WebCrashReport {
  const raw = error instanceof Error ? error.message : typeof error === 'string' ? error : String(error);
  const message = redactWebCrashText(raw);
  return { kind, app, message: message.length > MAX_MESSAGE ? `${message.slice(0, MAX_MESSAGE)}…` : message, at: new Date().toISOString() };
}

const consoleSink = (report: WebCrashReport): void => {
  console.error(`[vello:crash] ${report.kind} ${report.app}: ${report.message}`);
};

/** Record the page's uncaught errors and unhandled rejections; returns the uninstall. */
export function installWebCrashCapture(app: string, onCrash: (report: WebCrashReport) => void = consoleSink, target: Pick<Window, 'addEventListener' | 'removeEventListener'> = window): () => void {
  const onError = (event: Event): void => {
    const e = event as ErrorEvent;
    onCrash(describeWebCrash(e.error ?? e.message, 'global', app));
  };
  const onRejection = (event: Event): void => {
    onCrash(describeWebCrash((event as PromiseRejectionEvent).reason, 'unhandled-rejection', app));
  };
  target.addEventListener('error', onError);
  target.addEventListener('unhandledrejection', onRejection);
  return () => {
    target.removeEventListener('error', onError);
    target.removeEventListener('unhandledrejection', onRejection);
  };
}

const COPY: Readonly<Record<string, { title: string; body: string; retry: string; reload: string }>> = {
  en: { title: 'Something went wrong', body: 'This screen stopped. Nothing you saved was lost.', retry: 'Try again', reload: 'Reload the page' },
  pt: { title: 'Algo deu errado', body: 'Esta tela parou. Nada do que você salvou foi perdido.', retry: 'Tentar de novo', reload: 'Recarregar a página' },
  es: { title: 'Algo salió mal', body: 'Esta pantalla se detuvo. No se perdió nada de lo que guardaste.', retry: 'Intentar de nuevo', reload: 'Recargar la página' },
  it: { title: 'Qualcosa è andato storto', body: 'Questa schermata si è fermata. Nulla di ciò che hai salvato è andato perso.', retry: 'Riprova', reload: 'Ricarica la pagina' },
};

function pageLanguage(): string {
  const tag = (typeof document !== 'undefined' ? document.documentElement.lang : '') || (typeof navigator !== 'undefined' ? navigator.language : '') || 'en';
  const primary = tag.toLowerCase().split('-')[0] ?? 'en';
  return COPY[primary] !== undefined ? primary : 'en';
}

/** The default fallback: what went wrong in words, one primary "Try again", and a reload. */
export function CrashFallback({ reset, reload = () => window.location.reload() }: { readonly reset: () => void; readonly reload?: () => void }): ReactElement {
  const copy = COPY[pageLanguage()] ?? COPY.en!;
  return (
    <div role="alert" data-testid="vello-crash-fallback" style={{ maxWidth: 480, margin: '0 auto', padding: 'var(--space-10) var(--space-6)', display: 'grid', gap: 'var(--space-4)', textAlign: 'center' }}>
      <p style={{ margin: 0, color: 'var(--color-text-primary)', fontSize: 'var(--text-title-size)', fontWeight: 'var(--text-title-weight)' }}>{copy.title}</p>
      <p style={{ margin: 0, color: 'var(--color-text-secondary)', fontSize: 'var(--text-body-size)' }}>{copy.body}</p>
      <Button variant="primary" block onClick={reset} data-testid="vello-crash-retry">
        {copy.retry}
      </Button>
      <Button variant="ghost" block onClick={reload}>
        {copy.reload}
      </Button>
    </div>
  );
}

export interface AppErrorBoundaryProps {
  /** The app id stamped on every report (`admin`, `pdv`, …). */
  readonly app: string;
  readonly children?: ReactNode;
  readonly fallback?: (props: { readonly reset: () => void; readonly report: WebCrashReport }) => ReactNode;
  readonly onCrash?: (report: WebCrashReport) => void;
}

export class AppErrorBoundary extends Component<AppErrorBoundaryProps, { report?: WebCrashReport }> {
  override state: { report?: WebCrashReport } = {};

  static getDerivedStateFromError(error: unknown): { report: WebCrashReport } {
    return { report: describeWebCrash(error, 'render', '') };
  }

  override componentDidCatch(error: unknown, _info: ErrorInfo): void {
    (this.props.onCrash ?? consoleSink)(describeWebCrash(error, 'render', this.props.app));
  }

  private readonly reset = (): void => {
    this.setState({ report: undefined });
  };

  override render(): ReactNode {
    const { report } = this.state;
    if (report === undefined) return this.props.children;
    const full = { ...report, app: this.props.app };
    return this.props.fallback !== undefined ? this.props.fallback({ reset: this.reset, report: full }) : <CrashFallback reset={this.reset} />;
  }
}
